import {
  AssessmentDocumentArgs,
  Descendant,
  EditorDraft,
  EditorVersion,
  formatPhoneNumber,
  isDocumentType,
  isVersion2Draft,
  PatientCoverageId,
  SendFaxArgs,
  TemplateEditorDraft,
  TemplateId,
  templateKeyToCollection,
  templateTypeNames,
  UpdateCallback,
} from '@hb/shared'

import { useCallback, useMemo } from 'react'

import { addMetadata, useApp, usePracticeAccess } from '@hb/react'
import { useProfile } from '../../contexts/user/ProfileContext'
import { sendAssessmentDocument } from '../../hooks/backend/documents/utils'
import { sendFax } from '../../hooks/backend/faxes'
import { useUpdateDoc } from '../../hooks/backend/useUpdateDoc'
import { AssessmentDataProvider, TemplateInsurancePlansProvider } from './contexts'
import { TemplateViewModal } from './TemplateViewModal'
import { TemplateViewBodyProps } from './types'

export const ProfileTemplateViewModal = () => {
  const {
    selectedAssessment,
    assessmentId,
    viewedTemplate,
    patientRef,
    practicePatientRef,
    adminPatientRef,
    setViewedTemplateKey,
    user,
  } = useProfile()
  const { appName } = useApp()
  const { adminRef, populated, practiceRef } = selectedAssessment ?? {}
  const { selectedPracticeId } = usePracticeAccess()

  const handleFax = useCallback(
    async (data: Omit<SendFaxArgs, 'assessmentId' | 'category'>) => {
      if (!assessmentId) return { error: 'Internal error - missing assessment id' }
      if (!viewedTemplate) return { error: 'Internal error - missing viewed template' }
      try {
        await sendFax({
          ...data,
          assessmentId,
          category: viewedTemplate.type,
        })
        return {
          success: `Fax sent to ${formatPhoneNumber(data.faxToNumber)}.`,
        }
      } catch (error: any) {
        return {
          error: error?.message || 'An error occurred - post a message in the group chat!',
        }
      }
    },
    [assessmentId, viewedTemplate],
  )

  const onFax = useMemo(() => (appName === 'app' ? handleFax : undefined), [appName, handleFax])

  const draftRef = useMemo(() => {
    if (!viewedTemplate) return undefined
    if (appName === 'providers-app') {
      return viewedTemplate.type === 'consentForm' ? practicePatientRef : practiceRef
    }
    return viewedTemplate.type === 'consentForm' ? adminPatientRef : adminRef
  }, [appName, practicePatientRef, practiceRef, adminRef, adminPatientRef, viewedTemplate])

  const sendDocument = useCallback(
    async (args: AssessmentDocumentArgs) => {
      if (!args.type) return { error: 'Internal error - missing template type' }
      if (args.type === 'consentForm' && !args.templateId)
        return { error: 'Internal error - missing template id' }

      const documentTypeName = templateTypeNames[args.type]
      try {
        await sendAssessmentDocument({
          document: args,
        })
        const successMessage = `Successfully sent ${documentTypeName} document`
        // showSuccess(successMessage)
        setViewedTemplateKey(null)
        return { success: successMessage }
      } catch (err: any) {
        const errMessage = `Error sending document: ${err?.message || 'No message'}`
        // showError(errMessage)
        return { error: errMessage }
      }
    },
    [setViewedTemplateKey],
  )

  const handleSubmit = useCallback(
    async (
      text: Descendant[],
      editorVersion: EditorVersion,
      templateId: TemplateId | null,
      name: string,
      coverageId: PatientCoverageId | null,
    ): Promise<UpdateCallback> => {
      if (!viewedTemplate?.collection) {
        return { error: 'internal error' }
      }
      if (!isDocumentType(viewedTemplate.type) && viewedTemplate.type !== 'consentForm') {
        return { error: 'Not a sendable document type' }
      }
      if (appName === 'providers-app' && !selectedPracticeId) {
        return { error: 'Internal error - unable to get practice ID' }
      }
      const documentType = viewedTemplate.type
      if (!documentType) {
        return { error: 'internal error' }
      }

      if (!patientRef?.id) return { error: 'Internal error - no patient id' }
      if (!assessmentId && documentType !== 'consentForm')
        return { error: 'Internal error - no assessment id' }

      const document: AssessmentDocumentArgs = {
        assessmentId: assessmentId ?? null,
        patientId: patientRef.id,
        templateId,
        name,
        type: documentType,
        editorVersion,
        text,
        coverageId,
      }

      return sendDocument(document)
    },
    [viewedTemplate, assessmentId, patientRef, selectedPracticeId, appName, sendDocument],
  )

  const submitText = useMemo<TemplateViewBodyProps['submitText']>(() => {
    switch (viewedTemplate?.type) {
      case 'authAppeals':
        return { submit: 'Send Auth Appeal', confirm: 'Confirm send' }
      case 'authInstructions':
        return { submit: 'Send Auth Instructions', confirm: 'Confirm send' }
      case 'invoiceAndReceipt':
        return { submit: 'Send Invoice / Receipt', confirm: 'Confirm send' }
      case 'claims':
        return { submit: 'Send Claim Document', confirm: 'Confirm send' }
      case 'consentForm': {
        return { submit: 'Send Consent Form', confirm: 'Confirm send' }
      }
      default:
        return {
          submit: 'Error - unhandled viewed template collection',
          confirm: 'Error - unhandled viewed template collection',
        }
    }
  }, [viewedTemplate])

  const update = useUpdateDoc('draft')
  const canSubmit = useMemo(
    () =>
      viewedTemplate
        ? isDocumentType(viewedTemplate.type) || viewedTemplate.type === 'consentForm'
        : false,
    [viewedTemplate],
  )

  const baseDraft = useMemo(() => {
    const userDrafts = appName === 'providers-app' ? user?.practiceDrafts : user?.drafts
    if (viewedTemplate?.type === 'consentForm') {
      return userDrafts?.consentFormTemplates
    }
    if (!selectedAssessment) return undefined
    const collectionName = viewedTemplate ? templateKeyToCollection[viewedTemplate.type] : ''
    return collectionName ? populated?.drafts?.[collectionName] : undefined
  }, [selectedAssessment, viewedTemplate, user, populated, appName])

  const handleSave = useCallback(
    async (d: Descendant[], v: EditorVersion, templateId: TemplateId | null) => {
      if (!viewedTemplate) {
        return { error: 'internal error' }
      }
      const collectionName = templateKeyToCollection[viewedTemplate.type]
      const newDraft: TemplateEditorDraft = addMetadata(
        {
          templateType: viewedTemplate.type,
          templateId,
          text: d,
          editorVersion: v,
        },
        appName,
        !baseDraft,
      )
      return update(draftRef, `drafts.${collectionName}`, newDraft)
    },
    [draftRef, update, viewedTemplate, baseDraft, appName],
  )

  const draft = useMemo<EditorDraft | null>(() => {
    if (!baseDraft) return null
    return isVersion2Draft(baseDraft) ? baseDraft : { text: baseDraft, editorVersion: 'v1' }
  }, [baseDraft])

  return selectedAssessment && viewedTemplate ? (
    <TemplateInsurancePlansProvider>
      <AssessmentDataProvider editMode="document" type={viewedTemplate.type}>
        <TemplateViewModal
          isOpen={!!viewedTemplate}
          autoTemplateId={viewedTemplate.id}
          type={viewedTemplate.type}
          user={user}
          submitText={submitText}
          // overlayHeader={false}
          onClose={() => setViewedTemplateKey(null)}
          draft={draft}
          onSave={handleSave}
          onFax={appName !== 'providers-app' ? onFax : undefined}
          onSubmit={canSubmit ? handleSubmit : undefined}
        />
      </AssessmentDataProvider>
    </TemplateInsurancePlansProvider>
  ) : null
}
